import React from 'react';
import { Widget } from '../../../../../dashboard/components/Widget/Widget';
import type { WidgetConfig } from '../../../../../dashboard/components/Widget/Widget';
import { safeJsonParse } from '../../../../../utils/jsonRepairUtils';
import { ErrorBoundary } from './ErrorBoundary';
import { useDebouncedValue } from './useDebouncedValue';

interface MarkdownWidgetProps {
  code: string;
}

const WidgetRenderer = ({ code }: { code: string }) => {
  const widgetConfig: WidgetConfig = safeJsonParse<WidgetConfig>(
    code,
    'widget config',
  );
  return (
    <div className="m-4 my-4 font-sans">
      <Widget config={widgetConfig} />
    </div>
  );
};

export const MarkdownWidget: React.FC<MarkdownWidgetProps> = ({ code }) => {
  const debouncedCode = useDebouncedValue(code, 300, 500);

  return (
    <ErrorBoundary
      onError={(error, errorInfo) => {
        console.error(
          'Widget rendering error:',
          error,
          errorInfo,
          debouncedCode,
        );
      }}
      key={debouncedCode}
      fallback={
        <div className="my-4 overflow-hidden rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-900/5 transition-all duration-200 dark:bg-gray-900">
          <div className="animate-pulse">
            {/* Header skeleton */}
            <div className="mb-4">
              <div className="mb-2 h-4 w-24 rounded bg-gray-200 dark:bg-gray-700"></div>
            </div>

            {/* Content skeleton */}
            <div className="mb-4">
              <div className="mb-2 h-8 w-32 rounded bg-gray-200 dark:bg-gray-700"></div>
              <div className="h-3 w-20 rounded bg-gray-200 dark:bg-gray-700"></div>
            </div>

            {/* Footer skeleton */}
            <div className="flex items-center gap-2">
              <div className="h-6 w-16 rounded-full bg-gray-200 dark:bg-gray-700"></div>
              <div className="h-3 w-12 rounded bg-gray-200 dark:bg-gray-700"></div>
            </div>
          </div>
        </div>
      }
    >
      <WidgetRenderer code={debouncedCode} />
    </ErrorBoundary>
  );
};
